import { ILocalizationClient } from "@multiweb/clients/localization/ILocalizationClient";
import { ISectionLogger, LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ILocalizationItem } from "@multiweb/types/contracts/localizations";
import { ArrayUtils } from "Global/Base/Utils/ArrayUtils";
import { UNKNOWN_LOCALIZATION_CONTENT } from "./constants";
import { ILocalizationManager } from "./ILocalizationManager";
import { ILocalizationStorage } from "./ILocalizationStorage";

export class LocalizationManager implements ILocalizationManager {
    private static _emptyLocKeys: Set<string> = new Set(["", "XG-666", "666"]);

    private readonly _storage: ILocalizationStorage;
    private readonly _client: ILocalizationClient;

    /**
     * LazyLoadingSectionLogger potrebujeme kvuli cyklicke zavislosti. Vice viz `ServiceWorkerManager.createServiceWorker`.
     */
    private readonly _logger: ISectionLogger = LazyLoadingSectionLogger.create(LoggingSection.localizations);

    constructor(storage: ILocalizationStorage, client: ILocalizationClient) {
        this._storage = storage;
        this._client = client;
    }

    public getLocalizations(language: string, locKeys: readonly string[]): Promise<readonly ILocalizationItem[]> {
        return this._getLocalizations(language, locKeys);
    }

    /**
     * Načte požadované lokalizace. Kombinuje několik zdrojů a používá ty nejrychlejší.
     * @param language Kód jazyka lokalizací (např. 'EN' nebo 'CZ')
     * @param locKeys Lokalizační klíče k načtení
     * @param onLocalizationsPartLoaded Callback volaný při načtení části klíčů. Umožňuje průběžně reagovat na načtené lokalizace.
     */
    protected async _getLocalizations(language: string, locKeys: readonly string[], onLocalizationsPartLoaded?: (loadedLocalizations: readonly ILocalizationItem[]) => void): Promise<readonly ILocalizationItem[]> {
        if (locKeys.length === 0)
            return [];

        // jen unikátní a platné klíče
        let remainingLocKeys = [...this._getUniqueLocKeys(locKeys.filter(locKey => !LocalizationManager._emptyLocKeys.has(locKey)))];

        let loadedLocalizations: readonly ILocalizationItem[] = [];

        try {
            // jen unikátní a neplatné klíče
            const invalidLocKeys = [...this._getUniqueLocKeys(locKeys.filter(locKey => LocalizationManager._emptyLocKeys.has(locKey)))];
            if (invalidLocKeys && invalidLocKeys.length > 0) {
                // místo neplatných klíčů se nezobrazuje nic
                const localizationsInvalid = invalidLocKeys.map(locKey => ({
                    language,
                    locKey,
                    primaryText: "",
                    secondaryText: "",
                }));
                onLocalizationsPartLoaded?.(localizationsInvalid);

                loadedLocalizations = localizationsInvalid;
            }
        } catch (e) {
            this._logger.error(e);
        }

        if (remainingLocKeys.length > 0)
            try {
                // lokalizace z lokálního úložiště
                const localizationsFromStorage = await this._storage.getLocalizations(language, remainingLocKeys);
                if (localizationsFromStorage && localizationsFromStorage.length > 0) {
                    onLocalizationsPartLoaded?.(localizationsFromStorage);

                    loadedLocalizations = [...loadedLocalizations, ...localizationsFromStorage];
                    remainingLocKeys = ArrayUtils.difference(remainingLocKeys, localizationsFromStorage.map(localization => localization.locKey));
                }
            } catch (e) {
                this._logger.error(e);
            }

        if (remainingLocKeys.length > 0)
            try {
                // lokalizace z webové služby
                const localizationsFromService = await this._client.getLocalizations(language, remainingLocKeys);
                if (localizationsFromService && localizationsFromService.length > 0) {
                    onLocalizationsPartLoaded?.(localizationsFromService);

                    loadedLocalizations = [...loadedLocalizations, ...localizationsFromService];
                    remainingLocKeys = ArrayUtils.difference(remainingLocKeys, localizationsFromService.map(localization => localization.locKey));

                    await this._storage.saveLocalizations(language, localizationsFromService);
                }
            } catch (e) {
                this._logger.error(e);
            }

        if (remainingLocKeys.length > 0) {
            this._logger.warn("Localizations not found: " + JSON.stringify(remainingLocKeys));

            try {
                // položky, pro které se nepovedlo najít lokalizaci, obsahují otazníky (UNKNOWN_LOCALIZATION_CONTENT)
                const localizationsNotFound = remainingLocKeys.map(locKey => ({
                    language,
                    locKey,
                    primaryText: UNKNOWN_LOCALIZATION_CONTENT,
                    secondaryText: UNKNOWN_LOCALIZATION_CONTENT,
                }));
                onLocalizationsPartLoaded?.(localizationsNotFound);

                loadedLocalizations = [...loadedLocalizations, ...localizationsNotFound];
            } catch (e) {
                this._logger.error(e);
            }
        }

        return loadedLocalizations;
    }

    protected _getUniqueLocKeys(locKeys: readonly string[]): Set<string> {
        return new Set(locKeys);
    }
}
